import { defineRoute as route, flattenRoutes } from "./utils";

export const routes = {
  // Common routes
  root: route("/", { tools: true }),
  updatePassword: route("/update_password"),
  signOut: route("/sign_out", { header: false, footer: false }),
  error: route("/error", { tools: true }),

  // Teacher/student routes
  studentDashboard: route("/dashboard", { tools: true }),
  teacherDashboard: route("/teacher-dashboard", { tools: true }),
  levelTestScore: route("/test_score", { tools: true }),
  myProgress: route("/my_progress", { tools: true }),
  exercises: route("/exercises", { tools: true }),
  exercisesByCreator: route<{ creatorId: string }>("/exercises/by/:creatorId", {
    tools: true,
  }),
  exerciseCreate: route("/exercise/create", { tools: true }),
  exerciseEdit: route<IdParam>("/exercise/:id/edit", { tools: true }),
  exerciseDetails: route<IdParam>("/exercise/:id", { tools: true }),
  exerciseAssignments: route("/exercise/assignments", { tools: true }),
  exerciseAssignmentDetails: route<IdParam>("/exercise/assignments/:id", {
    tools: true,
  }),
  speakingClub: route("/speaking_club", { tools: true }),
  sessionFeedback: route<IdParam>("/feedback/:id", { tools: true }),
  sessionFeedbackEdit: route<IdParam>("/feedback/:id/edit", {
    tools: true,
  }),
  // `/settings` is a wildcard parent so the page can own nested section routes.
  // `path()` still builds "/settings", so every existing `routes.settings.path()`
  // call site keeps working unchanged.
  settings: route("/settings/*", { tools: true }, "/settings"),
  // Student sections
  settingsProfile: route("/settings/profile", { tools: true }),
  settingsBilling: route("/settings/billing", { tools: true }),
  settingsLearningPlan: route("/settings/learning-plan", { tools: true }),
  settingsInterests: route("/settings/interests", { tools: true }),
  settingsNotifications: route("/settings/notifications", { tools: true }),
  // Teacher sections
  settingsPayments: route("/settings/payments", { tools: true }),
  settingsTeachingBio: route("/settings/teaching-bio", { tools: true }),
  settingsTeachingPreferences: route("/settings/teaching-preferences", { tools: true }),
  settingsSchedule: route("/settings/schedule", { tools: true }),
  course: route("/course", { tools: true }),
  courseObject: route<IdParam>("/course/:id", { tools: true }),
  search: route("/search", { tools: true }),
  students: route("/students", { tools: true }),
  studentDetails: route<IdParam & { tab?: string }>("/student/:id/:tab?", {
    tools: true,
  }),
  instructorDetails: route<IdParam>("/instructor/:id", { tools: true }),
  sessionConversation: route<{ lessonId: string }>("/conversation/:lessonId", {
    header: false,
    footer: "small",
  }),
  postFeedback: route<{ lessonId: string }>("/conversation/:lessonId/post_feedback", {
    header: false,
    footer: "small",
  }),
  libraryRoot: route("/library", { tools: true }),
  libraryObject: route<IdParam>("/library/:id", { tools: true }),
  teamDashboard: route("/teams", { tools: true }),
  companyReportsPage: route<{ company_id: string }>("/company/:company_id/reports", {
    tools: true,
  }),
  teamDashboardParams: route<IdParam>("/teams/:id", { tools: true }),
  teamMemberDetails: route<{ id: string; member_id: string }>(
    "/teams/:id/member/:member_id",
    { tools: true }
  ),
  tagSearch: route<IdParam>("/tag/:id", { tools: true }),
  adaptiveTest: route("/adaptive-test", { tools: true }),
  practice: route<{ practiceId?: string }>("/practice/:practiceId?", {
    header: false,
    footer: false,
    tools: true,
  }),
  communicationSkills: route<{ skillCategoryId?: string }>(
    "/communication-skills/*",
    { tools: true },
    "/communication-skills/:skillCategoryId?"
  ),
  talkToTally: route<{ conversationId?: string }>("/talk-to-tally/:conversationId?", {
    header: false,
    footer: false,
    tools: true,
  }),
  payment: route<{ code?: string }>("/payment/:code?"),

  // Public routes
  public: {
    notFound: route("/not-found", { header: false, footer: false }),
    resetDone: route("/reset_done", { header: false, footer: false }),
    changePassword: route("/change_password", { header: false, footer: false }),
    signIn: route("/sign_in", { header: false, footer: false }),
    signInSso: route("/sign_in/sso", { header: false, footer: false }),
    signInOAuth: route<{ provider: string; step: string }>(
      "/sign_in/oauth/:provider/:step",
      { header: false, footer: false }
    ),
    forgot: route("/forgot", { header: false, footer: false }),
    passwordVerify: route("/passwd/verify", { header: false, footer: false }),
    verify: route("/verify", { header: false, footer: false }),
    selfSignupVerifyIndividual: route("/verify/individual", {
      header: false,
      footer: false,
    }),
    inviteVerify: route("/invite/verify", { header: false, footer: false }),
    selfSignUp: route("/self_sign_up", { header: false, footer: false }),
    signUpWithCode: route<{ code: string }>("/sign_up/code/:code", {
      header: false,
      footer: false,
    }),
    signUpSetupWithCode: route<{ code: string }>("/sign_up/setup/code/:code", {
      header: false,
      footer: false,
    }),
  },

  // Admin routes
  admin: {
    root: route("/admin/*", undefined, "/admin"),
    userList: route("/admin/users"),
    userDetails: route<IdParam>("/admin/users/:id"),
    companyList: route("/admin/companies"),
    companyDetails: route<IdParam>("/admin/companies/:id"),
    companyDashboard: route<{ companyId: string }>(
      "/admin/companies/:companyId/dashboard"
    ),
    companyTeamDetails: route<{ companyId: string; id: string }>(
      "/admin/companies/:companyId/teams/:id"
    ),
    teamList: route("/admin/teams"),
    teamDetails: route<IdParam>("/admin/teams/:id"),
    companyUserDetails: route<{ companyId: string; id: string }>(
      "/admin/companies/:companyId/users/:id"
    ),
    schoolList: route("/admin/schools"),
    schoolDetails: route<IdParam>("/admin/schools/:id"),
    calendar: route("/admin/calendar"),
    matching: route("/admin/matching"),
    sessionList: route("/admin/sessions"),
    sessionDetails: route<IdParam>("/admin/sessions/:id"),
    questionList: route("/admin/questions"),
    questionDetails: route<IdParam>("/admin/questions/:id"),
    monitoringShift: route("/admin/monitoring/shift"),
    settings: route("/admin/settings"),
    planList: route("/admin/plans"),
    planDetails: route<IdParam>("/admin/plans/:id"),
    exerciseList: route("/admin/exercises"),
    exerciseDetails: route<IdParam>("/admin/exercises/:id"),
    exerciseBuilder: route("/admin/builder/exercises"),
    tagList: route("/admin/tags"),
    tagDetails: route<IdParam>("/admin/tags/:id"),
    contentFolder: route("/admin/content", { tools: true }),
    editContentFile: route("/admin/content/edit", { tools: true }),
    importContentFile: route("/admin/content/object/import", { tools: true }),
    pricingModelList: route("/admin/pricing"),
    pricingModelDetails: route<IdParam>("/admin/pricing/:id"),
    workshopList: route("/admin/workshops"),
    workshopDetails: route<IdParam>("/admin/workshops/:id"),
    adaptiveTesting: route("/admin/adaptive-testing"),
    adaptiveTestingQuestionDetail: route<IdParam>(
      "/admin/adaptive-testing/questions/:id"
    ),
  },

  // Customer service routes
  customerServiceRoot: route("/customerservice"),
  customerServiceSessionList: route("/customerservice/sessions"),
  customerServiceSessionDetails: route<IdParam>("/customerservice/sessions/:id"),
} as const;

export const allRoutes = flattenRoutes(routes);

type IdParam = { id: string };

export const WEBSITE_ROUTES = {
  home: "https://www.talaera.com",
  privacyPolicy: "https://www.talaera.com/privacy/",
  termsOfService: "https://www.talaera.com/terms/",
  cookiePolicy: "https://www.talaera.com/cookies/",
  imprint: "https://www.talaera.com/imprint/",
  help: "http://help.talaera.com",
  teacherHub: "https://offers.talaera.com/teacher-hub",
  pricing: "https://www.talaera.com/pricing",
};

export const COOKIE_POLICY_URL = "/legal/cookies.html";
